/**
 * VideoEditorNode (Canvas V2)
 *
 * Configuration node for stitching video clips and audio in the Timeline Editor.
 * Rendered artifacts are created as separate Video Upload nodes.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Handle, Position, type NodeProps, useEdges, useNodes, useReactFlow } from 'reactflow'
import { Film, Maximize2, Minimize2, Music, Pause, Play, Plus, Scissors, Volume2, VolumeX } from 'lucide-react'
import { cancelNodeExecution, executeNode, getAssetUrl, getTypedNodeData, createNadDebug, type VideoEditorNodeData } from '@ponpon/api-client'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import {
  AgentNodeToolbar,
  useNodeActions,
  type ToolbarAction,
} from '../shared'
import { ReferenceSelector, type SelectedReference } from '../shared/ReferenceSelector'
import { getAudioAssetPath, getAudioOutputPort, getVideoOutputPort } from '../shared/mentionUtils'
import { TimelineEditorModal } from '../../panels/timeline/TimelineEditorModal'
import { TimelinePreviewPlayer, type TimelinePreviewControls } from '../../panels/timeline/TimelinePreviewPlayer'
import { StaticWaveform } from '../../panels/timeline/StaticWaveform'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { useCanvasOptional } from '../../CanvasContext'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { ExportConfirmSheet, buildDefaultExportName } from './ExportConfirmSheet'

export type { VideoEditorNodeData }

const NODE_WIDTH = 920
const REFERENCE_RAIL_WIDTH = 220
const PREVIEW_HEIGHT = (NODE_WIDTH - REFERENCE_RAIL_WIDTH) * (9 / 16)

const chromeLog = createNadDebug('dw:videoeditor-canvas-preview:chrome')

const renderLog = createNadDebug('dw:video-editor:canvas-render')

const exportFlowLog = createNadDebug('dw:video-editor:export-flow')

const STATUS_CLASS: Record<string, { dot: string; text: string }> = {
  idle: { dot: 'bg-text-muted', text: 'Ready' },
  running: { dot: 'bg-warning animate-pulse', text: 'Rendering...' },
  completed: { dot: 'bg-success', text: 'Done' },
  error: { dot: 'bg-destructive', text: 'Error' },
}

function referenceKey(ref: Pick<SelectedReference, 'nodeId' | 'mediaType'>): string {
  return `${ref.nodeId}:${ref.mediaType}`
}

function dedupeReferences(refs: SelectedReference[]): SelectedReference[] {
  const seenKeys = new Set<string>()
  const deduped: SelectedReference[] = []

  for (const ref of refs) {
    const key = referenceKey(ref)
    if (seenKeys.has(key)) continue
    seenKeys.add(key)
    deduped.push(ref)
  }

  return deduped
}

type ClipReference = {
  id: string
  path: string
  name: string
  // Upstream-known duration in seconds. video_generator nodes write
  // out_video_duration after each render — passing it through here lets
  // the Timeline Editor initialize per-clip durations correctly instead
  // of falling back to a hardcoded 5s placeholder while it async-probes
  // the video file. Undefined when the upstream doesn't expose a
  // duration (legacy nodes, uploads); the timeline then falls back to
  // probe-then-update.
  duration?: number
}
type AudioClipReference = ClipReference & { role?: 'original' | 'replacement' | 'sfx' }

function getAudioRole(node: { data?: object } | undefined): AudioClipReference['role'] {
  if (!node?.data || !('role' in node.data)) return undefined
  const role = node.data.role
  if (role === 'original' || role === 'replacement' || role === 'sfx') return role
  return undefined
}

function ClipThumbnail({ clip, mediaType }: { clip: ClipReference; mediaType: 'video' | 'audio' }) {
  const projectId = useProjectId()
  const baseUrl = useMemo(() => {
    if (!projectId) return null
    return getAssetUrl(projectId, clip.path)
  }, [projectId, clip.path])
  const mediaUrl = useAuthenticatedMediaUrl(baseUrl)

  return (
    <div
      className="min-w-0 rounded-lg border border-border-subtle bg-surface-1 overflow-hidden"
      title={clip.name}
      data-testid={`video-editor-${mediaType}-reference`}
    >
      <div className="h-14 bg-input-bg overflow-hidden">
        {mediaType === 'video' ? (
          mediaUrl ? (
            <video
              src={mediaUrl}
              className="h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Film className="w-4 h-4 text-text-muted" />
            </div>
          )
        ) : (
          <div className="h-full p-2">
            <StaticWaveform sourcePath={clip.path} barCount={22} />
          </div>
        )}
      </div>
      <div className="px-1.5 py-1">
        <span className="block truncate text-xs text-muted-foreground">{clip.name}</span>
      </div>
    </div>
  )
}

function ReferenceRail({
  videoClips,
  audioClips,
  hasClips,
  hasTimelineConfig,
  status,
  isCancelling,
  exportSuccess,
  onOpenReferenceSelector,
  onOpenEditor,
  onRender,
  onCancelRender,
  onExportChipClick,
}: {
  videoClips: ClipReference[]
  audioClips: AudioClipReference[]
  hasClips: boolean
  hasTimelineConfig: boolean
  status: string
  isCancelling: boolean
  exportSuccess: { nodeId: string; label: string } | null
  onOpenReferenceSelector: () => void
  onOpenEditor: () => void
  onRender: () => void
  onCancelRender: () => void
  onExportChipClick: (nodeId: string) => void
}) {
  return (
    <aside
      className="shrink-0 border-r border-border-subtle bg-surface-0 p-2 flex flex-col gap-2"
      style={{ width: REFERENCE_RAIL_WIDTH }}
      data-testid="video-editor-reference-rail"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">References</span>
        <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-xxs text-muted-foreground">
          {videoClips.length + audioClips.length}
        </span>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto nowheel">
        {videoClips.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xxs text-text-muted">
              <Film className="w-3 h-3" />
              <span>Video</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {videoClips.map((clip) => (
                <ClipThumbnail key={`video-${clip.id}-${clip.path}`} clip={clip} mediaType="video" />
              ))}
            </div>
          </div>
        )}

        {audioClips.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xxs text-text-muted">
              <Music className="w-3 h-3" />
              <span>Audio</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {audioClips.map((clip) => (
                <ClipThumbnail key={`audio-${clip.id}-${clip.path}`} clip={clip} mediaType="audio" />
              ))}
            </div>
          </div>
        )}

        {videoClips.length === 0 && audioClips.length === 0 && (
          <div className="rounded-lg border border-border-subtle bg-input-bg px-2 py-3 text-center">
            <span className="text-xs text-text-muted">No references</span>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpenReferenceSelector()
          }}
          className="h-7 w-full rounded-lg bg-surface-1 border border-input-border hover:bg-surface-3 hover:border-input-border-hover text-foreground text-xs font-medium flex items-center justify-center gap-1.5 transition-colors nodrag"
          data-testid="video-editor-add-references"
        >
          <Plus className="w-3.5 h-3.5" />
          Add References
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpenEditor()
          }}
          disabled={!hasClips || status === 'running'}
          className="h-7 w-full rounded-lg bg-surface-1 border border-input-border hover:bg-surface-3 hover:border-input-border-hover disabled:opacity-40 disabled:hover:bg-surface-1 disabled:hover:border-input-border text-foreground text-xs font-medium flex items-center justify-center gap-1.5 transition-colors nodrag"
          data-testid="video-editor-open-editor"
        >
          <Scissors className="w-3.5 h-3.5" />
          Open Editor
        </button>
        {status === 'running' ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCancelRender()
            }}
            disabled={isCancelling}
            className="h-7 w-full rounded-lg border border-destructive/50 bg-destructive/10 hover:bg-destructive/20 text-destructive text-xs font-medium flex items-center justify-center gap-1.5 transition-colors nodrag disabled:opacity-50"
            data-testid="video-editor-stop-render"
          >
            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
            Stop
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRender()
            }}
            disabled={!hasClips || !hasTimelineConfig}
            title={!hasTimelineConfig ? 'Open Editor to enable Export and Download' : undefined}
            className="h-7 w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:hover:bg-amber-500 disabled:cursor-not-allowed text-black text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors nodrag"
            data-testid="video-editor-export-button"
          >
            <Play className="w-3.5 h-3.5" />
            Export to Download
          </button>
        )}
        {exportSuccess && (
          <div
            className="pt-1 nodrag"
            data-testid="video-editor-export-success"
          >
            <p
              className="text-success text-[14px] leading-snug break-words"
              style={{ color: '#22c55e' }}
            >
              Export succeed!{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onExportChipClick(exportSuccess.nodeId)
                }}
                className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-success/15 hover:bg-success/25 text-success font-mono text-[12px] align-baseline transition-colors"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.18)', color: '#22c55e' }}
                data-testid="video-editor-export-success-chip"
                title={`Focus ${exportSuccess.label} on canvas`}
              >
                {exportSuccess.label}
              </button>{' '}
              created
            </p>
          </div>
        )}
      </div>
    </aside>
  )
}

function EditorPreviewSurface({
  nodeId,
  videoClips,
  audioClips,
  hasReferences,
  videoCount,
  audioCount,
  progressMessage,
  error,
  status,
  onOpenEditor,
}: {
  nodeId: string
  videoClips: ClipReference[]
  audioClips: AudioClipReference[]
  hasReferences: boolean
  videoCount: number
  audioCount: number
  progressMessage?: string | null
  error?: string | null
  status: string
  onOpenEditor: () => void
}) {
  if (status === 'running') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
        <div className="w-8 h-8 border-2 border-warning border-t-transparent rounded-full animate-spin" />
        <span className="text-xxs text-muted-foreground mt-2 text-center leading-relaxed">
          {progressMessage || 'Rendering...'}
        </span>
      </div>
    )
  }

  if (videoClips.length > 0) {
    return (
      <div className="absolute inset-0 nodrag">
        <TimelinePreviewPlayer
          nodeId={nodeId}
          videoClips={videoClips}
          audioClips={audioClips}
          readOnly
          hideClipBadge
          renderOverlays={(controls) => (
            <PreviewChrome
              controls={controls}
              videoCount={videoCount}
              completedMessage={status === 'completed' ? progressMessage : null}
            />
          )}
        />
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-1 border border-border-subtle text-muted-foreground">
        <Scissors className="w-5 h-5" />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium text-foreground">
          {hasReferences ? 'Timeline configuration' : 'Connect references'}
        </div>
        <div className="text-xs text-muted-foreground">
          {hasReferences
            ? `${videoCount} video reference${videoCount === 1 ? '' : 's'} and ${audioCount} audio reference${audioCount === 1 ? '' : 's'} connected`
            : 'Add video and audio references to begin editing'}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onOpenEditor()
        }}
        disabled={!hasReferences}
        className="h-9 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:hover:bg-primary text-white text-sm font-medium px-4 flex items-center gap-2 transition-colors nodrag"
        data-testid="video-editor-open-editor-main"
      >
        <Scissors className="w-4 h-4" />
        Open Editor
      </button>
      {error && (
        <span className="text-xs text-destructive line-clamp-2 cursor-help" title={error}>
          {sanitizeNodeError(error)}
        </span>
      )}
    </div>
  )
}

function PreviewChrome({
  controls,
  videoCount,
  completedMessage,
}: {
  controls: TimelinePreviewControls
  videoCount: number
  completedMessage: string | null | undefined
}) {
  const {
    isPlaying,
    togglePlayPause,
    playheadPosition,
    totalDuration,
    setPlayhead,
    currentClipIndex,
    currentClipName,
    isMuted,
    toggleMute,
    previewElementRef,
  } = controls

  const [isFullscreen, setIsFullscreen] = useState(false)
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const handleMute = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      const next = !isMuted
      chromeLog({ event: 'mute-toggle', muted: next })
      toggleMute()
    },
    [isMuted, toggleMute],
  )

  const handleFullscreen = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      const wrapper = previewElementRef.current
      if (!wrapper) return
      const entering = !document.fullscreenElement
      chromeLog({ event: 'fullscreen-toggle', entering })
      try {
        if (entering) {
          await wrapper.requestFullscreen()
        } else {
          await document.exitFullscreen()
        }
      } catch (err) {
        // Browsers may reject fullscreen in non-user-gesture contexts.
        // We still emit the trace so debugging is possible.
        // eslint-disable-next-line no-console
        console.warn('VideoEditor preview fullscreen rejected', err)
      }
    },
    [previewElementRef],
  )

  const handleScrub = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const t = parseFloat(event.target.value)
      if (Number.isFinite(t)) setPlayhead(t)
    },
    [setPlayhead],
  )

  const handlePlayClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      togglePlayPause()
    },
    [togglePlayPause],
  )

  const badgeIndex = currentClipIndex == null ? 1 : currentClipIndex + 1

  return (
    <>
      {/* Top-left clip badge — always visible */}
      <div
        className="pointer-events-none absolute top-2 left-2 z-10 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-sm border border-white/10"
        data-testid="video-editor-clip-badge"
      >
        <p className="text-foreground text-xs font-medium leading-tight">
          {currentClipName || 'Loading...'}
        </p>
        <p className="text-text-muted text-xxs leading-tight">
          Clip {badgeIndex} of {videoCount}
        </p>
      </div>

      {/* Top-right mute + fullscreen */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        <button
          type="button"
          onClick={handleMute}
          className="h-7 w-7 rounded-md bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/10 text-foreground/85 hover:text-foreground flex items-center justify-center nodrag"
          data-testid="video-editor-preview-mute"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={handleFullscreen}
          className="h-7 w-7 rounded-md bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/10 text-foreground/85 hover:text-foreground flex items-center justify-center nodrag"
          data-testid="video-editor-preview-fullscreen"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      </div>

      {completedMessage && (
        <div className="absolute top-12 right-2 z-10 max-w-[55%] bg-black/65 backdrop-blur-sm text-foreground text-xxs px-2 py-1 rounded truncate">
          {completedMessage}
        </div>
      )}

      {/* PREVIEW badge — bottom-left of preview frame, above bottom bar */}
      <div
        className="pointer-events-none absolute bottom-10 left-2 z-10 px-2 py-0.5 rounded-md bg-amber-500/85 text-black text-[10px] font-bold tracking-wider shadow-md backdrop-blur-sm"
        data-testid="video-editor-preview-badge"
      >
        PREVIEW
      </div>

      {/* Bottom timeline bar — VideoPreviewPlayer-style */}
      <div
        className="absolute bottom-0 left-0 right-0 z-10 px-2 py-1.5 bg-gradient-to-t from-black/80 to-transparent flex items-center gap-1.5 nodrag"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        data-testid="video-editor-bottom-bar"
      >
        <button
          type="button"
          onClick={handlePlayClick}
          className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-foreground hover:text-white transition-colors"
          data-testid="video-editor-preview-play"
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>
        <span
          className="text-text-secondary text-[10px] tabular-nums flex-shrink-0 select-none"
          data-testid="video-editor-current-time"
        >
          {formatPreviewTime(playheadPosition)}
        </span>
        <div className="flex-1 h-5 flex items-center group/scrub relative">
          <div className="w-full h-1 bg-white/20 rounded-full relative group-hover/scrub:h-1.5 transition-all">
            <div
              className="absolute top-0 left-0 h-full bg-white/70 rounded-full pointer-events-none"
              style={{
                width: `${
                  totalDuration > 0
                    ? Math.min(100, Math.max(0, (playheadPosition / totalDuration) * 100))
                    : 0
                }%`,
              }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/scrub:opacity-100 transition-opacity pointer-events-none"
              style={{
                left: `${
                  totalDuration > 0
                    ? Math.min(100, Math.max(0, (playheadPosition / totalDuration) * 100))
                    : 0
                }%`,
                transform: `translateX(-50%) translateY(-50%)`,
              }}
              data-testid="video-editor-preview-playhead"
            />
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(totalDuration, 0.01)}
            step={0.01}
            value={Math.min(playheadPosition, Math.max(totalDuration, 0.01))}
            onChange={handleScrub}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="video-editor-preview-scrubber"
            aria-label="Seek video preview"
          />
        </div>
        <span
          className="text-text-secondary text-[10px] tabular-nums flex-shrink-0 select-none"
          data-testid="video-editor-total-duration"
        >
          {formatPreviewTime(totalDuration)}
        </span>
      </div>
    </>
  )
}

/** Format seconds as M:SS — mirrors VideoPreviewPlayer's formatTime. */
function formatPreviewTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const VideoEditorNode = memo(function VideoEditorNode(props: NodeProps<VideoEditorNodeData>) {
  const { id, data, selected, dragging } = props
  const { status = 'idle', progressMessage, error } = data
  const projectId = useProjectId()
  const uploadFile = useUploadFile()
  const canvas = useCanvasOptional()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const allNodes = useNodes<ReactFlowNodeData>()
  const reactFlow = useReactFlow()
  const { handleDelete, handleDuplicate } = useNodeActions({ nodeId: id })

  const [isTimelineEditorOpen, setIsTimelineEditorOpen] = useState(false)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isExportSheetOpen, setIsExportSheetOpen] = useState(false)
  const [exportSheetDefaultName, setExportSheetDefaultName] = useState(() => buildDefaultExportName())
  const handleOpenReferenceSelector = useCallback(() => setIsRefSelectorOpen(true), [])

  const resolvedVideoClips = useMemo(() => {
    const clipEdges = edges.filter(
      (e) =>
        e.target === id &&
        (e.targetHandle === 'in_videos' || e.targetHandle === 'in_video'),
    )
    const clips: ClipReference[] = []
    for (const edge of clipEdges) {
      const sourceNode = allNodes.find((n) => n.id === edge.source)
      const sourceData = sourceNode ? getTypedNodeData(sourceNode, 'videoGenerator') : undefined
      const genVideo = sourceData?.out_generated_video
      const pickedIdx = genVideo?.picked_index ?? 0
      const genVideoPath = genVideo?.history?.[pickedIdx]?.result?.local_path
      // Also pick up videoUpload (`video` node type) outputs and any other
      // node that exposes an `out_video` / `out_remixed_video` field — the
      // typed accessor returns undefined for non-videoGenerator types so we
      // fall through to the videoUpload typed accessor.
      const uploadData = sourceNode ? getTypedNodeData(sourceNode, 'video') : undefined
      const videoPath: string | undefined =
        genVideoPath ||
        sourceData?.out_video ||
        sourceData?.out_remixed_video ||
        uploadData?.out_video

      if (videoPath) {
        // Read upstream out_video_duration so the Timeline Editor can show
        // the real clip length immediately instead of a 5s placeholder
        // until the async file probe completes.  video_generator writes
        // out_video_duration after each render; legacy/upload nodes may
        // not have it, in which case we leave duration undefined and the
        // timeline falls back to probe.
        const upstreamDuration =
          typeof sourceData?.out_video_duration === 'number' &&
          sourceData.out_video_duration > 0
            ? sourceData.out_video_duration
            : undefined
        clips.push({
          id: edge.source,
          path: videoPath,
          name: videoPath.split('/').pop() || 'Untitled',
          duration: upstreamDuration,
        })
      }
    }
    return clips
  }, [edges, id, allNodes])

  const resolvedAudioClips = useMemo(() => {
    const audioEdges = edges.filter(
      (e) =>
        e.target === id &&
        (e.targetHandle === 'in_audios' || e.targetHandle === 'in_audio'),
    )
    const clips: AudioClipReference[] = []
    for (const edge of audioEdges) {
      const sourceNode = allNodes.find((n) => n.id === edge.source)
      const audioPath = getAudioAssetPath(sourceNode?.type ?? '', sourceNode?.data)

      if (audioPath) {
        clips.push({
          id: edge.source,
          path: audioPath,
          name: audioPath.split('/').pop() || 'Untitled',
          role: getAudioRole(sourceNode),
        })
      }
    }
    return clips
  }, [edges, id, allNodes])

  const currentReferences = useMemo(() => {
    const refs: SelectedReference[] = []
    for (const clip of resolvedVideoClips) {
      const node = allNodes.find((n) => n.id === clip.id)
      refs.push({
        nodeId: clip.id,
        nodeType: node?.type ?? 'video',
        label: clip.name,
        assetPath: clip.path,
        mediaType: 'video',
      })
    }
    for (const clip of resolvedAudioClips) {
      const node = allNodes.find((n) => n.id === clip.id)
      refs.push({
        nodeId: clip.id,
        nodeType: node?.type ?? 'audio',
        label: clip.name,
        assetPath: clip.path,
        mediaType: 'audio',
      })
    }
    return dedupeReferences(refs)
  }, [resolvedVideoClips, resolvedAudioClips, allNodes])

  const handleRefSave = useCallback(
    (selected: SelectedReference[]) => {
      setIsRefSelectorOpen(false)

      const refs = dedupeReferences(selected)
      const videoRefs = refs.filter((ref) => ref.mediaType === 'video')
      const audioRefs = refs.filter((ref) => ref.mediaType === 'audio')

      canvas?.replacePortEdges(
        id,
        'in_videos',
        videoRefs
          .map((ref, index) => ref.nodeId && !ref.nodeId.startsWith('ref-') ? ({
            source: ref.nodeId,
            sourceHandle: getVideoOutputPort(ref.nodeType),
            toIndex: index,
          }) : null)
          .filter((edge): edge is { source: string; sourceHandle: string; toIndex: number } => edge !== null),
      )
      canvas?.replacePortEdges(
        id,
        'in_audios',
        audioRefs
          .map((ref, index) => ref.nodeId && !ref.nodeId.startsWith('ref-') ? ({
            source: ref.nodeId,
            sourceHandle: getAudioOutputPort(ref.nodeType),
            toIndex: index,
          }) : null)
          .filter((edge): edge is { source: string; sourceHandle: string; toIndex: number } => edge !== null),
      )
      canvas?.replacePortEdges(id, 'in_video', [])
      canvas?.replacePortEdges(id, 'in_audio', [])
    },
    [canvas, id],
  )

  const statusConfig = STATUS_CLASS[status] || STATUS_CLASS.idle
  const hasClips = resolvedVideoClips.length > 0
  const hasReferences = hasClips || resolvedAudioClips.length > 0
  const hasTimelineConfig = Boolean(data.in_timeline_config)

  // Existing labels on the canvas — used for duplicate-name validation
  // in the Export confirm sheet. Excludes this editor node's own label.
  const existingLabels = useMemo(() => {
    const labels = new Set<string>()
    for (const node of allNodes) {
      if (node.id === id) continue
      const rawLabel = node.data?.label
      const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : node.id
      if (label) labels.add(label)
    }
    return labels
  }, [allNodes, id])

  // Open the Export confirm sheet.
  const handleOpenExportSheet = useCallback(() => {
    if (!projectId || status === 'running') return
    const defaultName = buildDefaultExportName()
    setExportSheetDefaultName(defaultName)
    setIsExportSheetOpen(true)
    exportFlowLog({
      event: 'open-export-sheet',
      origin: 'canvas',
      nodeId: id,
      defaultName,
      existingLabelsCount: existingLabels.size,
    })
  }, [projectId, status, id, existingLabels])

  // Confirm sheet → write the chosen name to YDoc, then dispatch executeNode.
  const handleConfirmExport = useCallback(
    async (chosenName: string) => {
      if (!projectId) return
      setIsExportSheetOpen(false)
      const timelineConfig = data.in_timeline_config
      const inputUpdates = timelineConfig
        ? { in_timeline_config: timelineConfig, in_export_name: chosenName }
        : { in_export_name: chosenName }
      exportFlowLog({
        event: 'confirm-export',
        nodeId: id,
        chosenName,
        hasTimelineConfig: Boolean(timelineConfig),
      })
      try {
        canvas?.updateNodeData(id, { in_export_name: chosenName })
        renderLog({
          event: 'canvas-render-requested',
          nodeId: id,
          projectId,
          chosenName,
          hasInputUpdates: true,
          hasTimelineConfig: Boolean(timelineConfig),
        })
        await executeNode(projectId, id, 'timeline_render', undefined, inputUpdates)
        renderLog({ event: 'canvas-render-dispatched', nodeId: id, hasInputUpdates: true })
      } catch (err) {
        renderLog({ event: 'canvas-render-failed', nodeId: id, error: String(err) })
        console.error('Canvas render failed:', err)
      }
    },
    [projectId, id, canvas, data.in_timeline_config],
  )

  // Toolbar "Re-stitch" — fires `timeline_render` against the existing
  // `in_timeline_config` so the user can re-run the stitch after upstream
  // clips updated without reopening the timeline editor or the export
  // confirm sheet. Re-uses the prior `in_export_name` when present so the
  // re-stitched output overwrites in place; falls back to a fresh default
  // name if the editor has never been exported.
  // Disabled when there are no clips, no timeline config, or the node is
  // currently running.
  //
  // Overwrite confirmation: when ``data.last_export_node_id`` points at an
  // existing canvas node, Re-stitch will replace that node's video in
  // place. Ask once before overwriting so the user can choose to use the
  // full Export sheet (which generates a fresh name) instead. This is the
  // single load-bearing warning — the Export-to-Download button already
  // handles fresh-name flow via the confirm sheet.
  const lastExportNodeId =
    typeof data.last_export_node_id === 'string' && data.last_export_node_id
      ? data.last_export_node_id
      : undefined
  // Existence check is computed at call time via reactFlow.getNode() so it
  // doesn't recompute on every pan/zoom/selection — ``allNodes`` from
  // ``useNodes()`` changes on every store mutation, which would have
  // re-fired the memo + toolbar useMemo on every wheel tick. The
  // existence answer is only read inside handleReStitch and inside the
  // toolbar label, both of which are computed lazily.
  //
  // Closure correctness: ``lastExportNodeId`` is captured by reference
  // but the dep array re-creates the callback whenever the source field
  // changes, so a stale id from a prior render can never sneak through.
  // ``reactFlow`` itself is a stable instance ref (ReactFlow recreates
  // it only on instance reset), so listing it in deps doesn't cause
  // extra re-fires under normal canvas use.
  const wouldOverwriteExport = useCallback(
    () => Boolean(lastExportNodeId && reactFlow.getNode(lastExportNodeId)),
    [lastExportNodeId, reactFlow],
  )

  const handleReStitch = useCallback(async () => {
    if (!projectId) return
    if (!hasClips || status === 'running') return
    const overwriting = wouldOverwriteExport()
    if (overwriting) {
      const ok = window.confirm(
        'Re-stitch will replace the previous final cut in place. ' +
          'Cancel and pick "Export to Download" if you want a fresh copy.',
      )
      if (!ok) {
        renderLog({ event: 'canvas-restitch-cancelled-by-user', nodeId: id })
        return
      }
    }
    // Synthesise a default timeline_config when the user never opened the
    // timeline editor (e.g. agent-auto-stitched editors created by
    // create_video_nodes_from_storyboard). Without this fallback the
    // Re-stitch button is disabled post-generation until the user opens
    // and saves the editor manually. The synthesised config is the
    // simplest valid stitch: each clip in upstream order, back-to-back
    // on track 0, no fades. ``_render_timeline_from_config`` refreshes
    // each clip's source_path from the live ``in_videos`` edges, so
    // stale paths don't matter — only the topology (which clips, in what
    // order) needs to be right.
    //
    // Audio: review #4 — if the user wired ``in_audios`` edges (music /
    // SFX / replacement dialogue) after the auto-stitch, those would be
    // silently dropped without audio_tracks here. Mirror the video clip
    // shape: one audio_tracks entry per resolved audio edge on track 0,
    // and bump ``audio_track_count`` to match so the renderer allocates
    // the right number of muxer inputs. Video-clip-embedded Seedance
    // dialogue keeps muxing through regardless (it lives inside each
    // clip's MP4, not in audio_tracks).
    const timelineConfig: NonNullable<VideoEditorNodeData['in_timeline_config']> =
      data.in_timeline_config ?? {
        operation: 'timeline_render',
        timeline: {
          clips: resolvedVideoClips.map((clip, index) => ({
            id: clip.id,
            source_path: clip.path,
            order: index,
            track_index: 0,
          })),
          audio_tracks: resolvedAudioClips.map((clip) => ({
            source_path: clip.path,
            role: clip.role === 'replacement' ? 'replacement' : clip.role === 'sfx' ? 'sfx' : 'original',
            track_index: 0,
          })),
          video_track_count: 1,
          audio_track_count: resolvedAudioClips.length > 0 ? 1 : 0,
        },
      }
    const exportName =
      typeof data.in_export_name === 'string' && data.in_export_name
        ? data.in_export_name
        : buildDefaultExportName()
    const inputUpdates = {
      in_timeline_config: timelineConfig,
      in_export_name: exportName,
    }
    renderLog({
      event: 'canvas-restitch-requested',
      nodeId: id,
      projectId,
      exportName,
      hasTimelineConfig: Boolean(timelineConfig),
      wouldOverwriteExport: overwriting,
    })
    try {
      await executeNode(projectId, id, 'timeline_render', undefined, inputUpdates)
      renderLog({ event: 'canvas-restitch-dispatched', nodeId: id })
    } catch (err) {
      renderLog({ event: 'canvas-restitch-failed', nodeId: id, error: String(err) })
      console.error('Re-stitch failed:', err)
    }
  }, [
    projectId,
    id,
    hasClips,
    status,
    wouldOverwriteExport,
    data.in_timeline_config,
    data.in_export_name,
    resolvedVideoClips,
    resolvedAudioClips,
  ])

  // Cancel the confirm sheet (no execute).
  const handleCancelExport = useCallback(() => {
    setIsExportSheetOpen(false)
    exportFlowLog({ event: 'cancel-export-sheet', nodeId: id })
  }, [id])

  // Chip click in the success line → fit the new node into view + select it.
  const handleExportChipClick = useCallback(
    (exportNodeId: string) => {
      const node = reactFlow.getNode(exportNodeId)
      exportFlowLog({ event: 'chip-click', nodeId: id, exportNodeId, found: !!node })
      if (!node) return
      reactFlow.fitView({ nodes: [node], padding: 0.3, duration: 500 })
      reactFlow.setNodes((nodes) =>
        nodes.map((n) => ({ ...n, selected: n.id === exportNodeId })),
      )
    },
    [reactFlow, id],
  )

  // Canvas cancel handler
  const handleCancelRender = useCallback(async () => {
    if (!projectId || isCancelling) return
    renderLog({ event: 'canvas-cancel-requested', nodeId: id })
    setIsCancelling(true)
    try {
      await cancelNodeExecution(projectId, id)
      renderLog({ event: 'canvas-cancel-dispatched', nodeId: id })
    } catch (err) {
      renderLog({ event: 'canvas-cancel-failed', nodeId: id, error: String(err) })
      console.error('Cancel render failed:', err)
    } finally {
      setIsCancelling(false)
    }
  }, [projectId, id, isCancelling])

  // Persisted export-success state.  `last_export_node_id` is written by
  // the backend agent after a successful export; the chip label is looked
  // up live from the canvas so renames stay in sync.
  const exportSuccess = useMemo(() => {
    const exportNodeId = data.last_export_node_id
    if (!exportNodeId) return null
    const node = allNodes.find((n) => n.id === exportNodeId)
    if (!node) {
      // Node may not have streamed in yet (or was deleted) — still surface
      // the success line keyed by node id so reload doesn't drop it.
      return { nodeId: exportNodeId, label: exportNodeId }
    }
    const rawLabel = node.data?.label
    const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : exportNodeId
    return { nodeId: exportNodeId, label }
  }, [data.last_export_node_id, allNodes])

  // Trace render-completion transitions for log-based debugging.  Note:
  // the visible success indicator is `exportSuccess` (derived from
  // YDoc-persisted `last_export_node_id`) — there is no toast and no
  // auto-fitView here, so the user is in control of any pan/zoom.
  const prevStatusRef = useRef(status)
  useEffect(() => {
    if (status === 'running' && prevStatusRef.current !== 'running') {
      renderLog({ event: 'render-status-running', nodeId: id })
    }
    if (status === 'completed' && prevStatusRef.current === 'running') {
      renderLog({
        event: 'render-status-completed',
        nodeId: id,
        lastExportNodeId: data.last_export_node_id ?? null,
      })
      exportFlowLog({
        event: 'render-completed',
        nodeId: id,
        lastExportNodeId: data.last_export_node_id ?? null,
      })
    }
    prevStatusRef.current = status
  }, [status, id, data.last_export_node_id])

  // Trace persisted-success rehydration on mount so debug logs show
  // whether the success line survived a reload.
  useEffect(() => {
    if (exportSuccess) {
      exportFlowLog({
        event: 'rehydrate-success',
        nodeId: id,
        exportNodeId: exportSuccess.nodeId,
        label: exportSuccess.label,
      })
    }
    // Run once per `last_export_node_id` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.last_export_node_id])

  const toolbarActions = useMemo<ToolbarAction[]>(
    () => [
      {
        key: 'open-editor',
        label: 'Open Editor',
        icon: <Scissors className="w-3.5 h-3.5" />,
        onClick: () => setIsTimelineEditorOpen(true),
        variant: 'primary',
        disabled: !hasClips || status === 'running',
      },
      {
        key: 'restitch',
        // Best-effort hint — eager check in handleReStitch confirms before
        // overwriting even if the label is stale (e.g. user just deleted
        // the prior export node without re-opening the toolbar).
        label: lastExportNodeId ? 'Re-stitch (overwrites)' : 'Re-stitch',
        icon: <Play className="w-3.5 h-3.5" />,
        onClick: handleReStitch,
        // ``hasTimelineConfig`` is no longer in the disabled condition —
        // handleReStitch synthesises a default config from resolvedVideoClips
        // when the user never opened the timeline editor (e.g. agent-
        // auto-stitched editors). Without this relaxation the button was
        // permanently disabled on auto-created editors until the user
        // manually opened + saved the editor.
        disabled: !hasClips || status === 'running',
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    // hasTimelineConfig is not read in this memo (it was dropped from the
    // disabled condition above) — listing it just churns the memo.
    [hasClips, status, lastExportNodeId, handleDuplicate, handleDelete, handleReStitch],
  )

  return (
    <div className="relative">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      <div
        className={`rounded-lg shadow transition-all bg-card border group relative ${
          selected ? 'ring-2 ring-white/70 border-white/40' : 'border-border'
        }`}
        style={{ width: NODE_WIDTH }}
      >
        <Handle
          type="target"
          position={Position.Top}
          id="in_videos"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_audios"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />

        <div className="px-3 py-2 bg-card-hover border-b border-border flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground">Video Editor</span>
            {hasReferences && (
              <span className="text-xxs text-muted-foreground bg-surface-1 px-1.5 py-0.5 rounded">
                {resolvedVideoClips.length} video / {resolvedAudioClips.length} audio
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xxs text-muted-foreground">{statusConfig.text}</span>
            <div className={`w-2.5 h-2.5 rounded-full ${statusConfig.dot}`} />
          </div>
        </div>

        <div className="flex" style={{ height: PREVIEW_HEIGHT }}>
          <ReferenceRail
            videoClips={resolvedVideoClips}
            audioClips={resolvedAudioClips}
            hasClips={hasClips}
            hasTimelineConfig={hasTimelineConfig}
            status={status}
            isCancelling={isCancelling}
            exportSuccess={exportSuccess}
            onOpenReferenceSelector={handleOpenReferenceSelector}
            onOpenEditor={() => setIsTimelineEditorOpen(true)}
            onRender={handleOpenExportSheet}
            onCancelRender={handleCancelRender}
            onExportChipClick={handleExportChipClick}
          />

          <div
            className="relative flex-1 bg-canvas overflow-hidden"
            style={{ height: PREVIEW_HEIGHT }}
            data-testid="video-editor-config-surface"
          >
            <EditorPreviewSurface
              nodeId={id}
              videoClips={resolvedVideoClips}
              audioClips={resolvedAudioClips}
              hasReferences={hasReferences}
              videoCount={resolvedVideoClips.length}
              audioCount={resolvedAudioClips.length}
              progressMessage={progressMessage}
              error={error}
              status={status}
              onOpenEditor={() => setIsTimelineEditorOpen(true)}
            />
          </div>
        </div>
      </div>

      {isTimelineEditorOpen && (
        <TimelineEditorModal
          nodeId={id}
          videoClips={resolvedVideoClips}
          audioClips={resolvedAudioClips}
          nodeStatus={status}
          nodeProgressMessage={progressMessage}
          onClose={() => setIsTimelineEditorOpen(false)}
          onOpenReferenceSelector={handleOpenReferenceSelector}
        />
      )}

      <ReferenceSelector
        isOpen={isRefSelectorOpen}
        onClose={() => setIsRefSelectorOpen(false)}
        onSave={handleRefSave}
        excludeNodeId={id}
        canvasNodes={allNodes}
        currentReferences={currentReferences}
        maxReferences={20}
        targetBackendType="video_editor"
        onUploadFile={uploadFile ?? undefined}
      />

      <ExportConfirmSheet
        isOpen={isExportSheetOpen}
        defaultName={exportSheetDefaultName}
        existingLabels={existingLabels}
        onCancel={handleCancelExport}
        onConfirm={handleConfirmExport}
      />
    </div>
  )
})

VideoEditorNode.displayName = 'VideoEditorNode'
